import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { Plus, Bot, ToggleLeft, ToggleRight } from "lucide-react";

const AdminAgents = () => {
  const { toast } = useToast();
  const [agents, setAgents] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchAgents(); }, []);

  const fetchAgents = async () => {
    setLoading(true);
    const { data } = await supabase.from("agents").select("*").order("created_at", { ascending: false });
    setAgents(data || []);
    setLoading(false);
  };

  const toggleActive = async (agent: any) => {
    const { error } = await supabase.from("agents").update({ is_active: !agent.is_active }).eq("id", agent.id);
    if (error) {
      toast({ title: "Erro ao atualizar", variant: "destructive" });
    } else {
      fetchAgents();
    }
  };

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Agentes</h1>
          <p className="text-sm text-muted-foreground">Gerencie seus agentes de conteúdo</p>
        </div>
        <Link to="/admin/agents/new">
          <Button size="sm"><Plus className="mr-1 h-4 w-4" /> Novo Agente</Button>
        </Link>
      </div>

      {loading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map((i) => <div key={i} className="h-40 animate-pulse rounded-xl bg-muted" />)}
        </div>
      ) : agents.length === 0 ? (
        <div className="py-20 text-center">
          <Bot className="mx-auto mb-4 h-12 w-12 text-muted-foreground/50" />
          <p className="text-muted-foreground mb-4">Nenhum agente criado ainda.</p>
          <Link to="/admin/agents/new"><Button>Criar primeiro agente</Button></Link>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {agents.map((agent) => (
            <div key={agent.id} className="rounded-xl border border-border bg-card p-5 shadow-sm transition-shadow hover:shadow-md">
              <div className="mb-3 flex items-start justify-between">
                <div className="flex items-center gap-2">
                  <Bot className="h-5 w-5 text-primary" />
                  <h3 className="font-semibold text-foreground">{agent.name}</h3>
                </div>
                <button onClick={() => toggleActive(agent)} className="text-muted-foreground hover:text-foreground">
                  {agent.is_active ? <ToggleRight className="h-6 w-6 text-green-500" /> : <ToggleLeft className="h-6 w-6" />}
                </button>
              </div>
              <p className="mb-1 text-sm text-muted-foreground"><strong>Nicho:</strong> {agent.niche}</p>
              <p className="mb-1 text-sm text-muted-foreground"><strong>Tom:</strong> {agent.tone_of_voice}</p>
              {agent.target_audience && (
                <p className="mb-3 text-sm text-muted-foreground"><strong>Público:</strong> {agent.target_audience}</p>
              )}
              <div className="flex items-center justify-between">
                <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${agent.is_active ? "bg-green-100 text-green-700" : "bg-muted text-muted-foreground"}`}>
                  {agent.is_active ? "Ativo" : "Inativo"}
                </span>
                <Link to={`/admin/agents/${agent.id}/edit`}>
                  <Button variant="ghost" size="sm">Editar</Button>
                </Link>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminAgents;
